import { type ForwardedRef, forwardRef, memo, type MouseEvent } from "react";
import { Flex } from "../../flex";
import { Tooltip } from "../../tooltip/tooltip";
import { RedoButton, type RedoButtonSize } from "../buttons/redo-button";
import type { RedoCommandMenuItem } from "./redo-command-menu";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";
import { RedoCommandShortcut } from "./redo-command-shortcut";

/**
 * @param indentForIcon if at least one of the menu items has an icon,
 * we want to space all the buttons evenly
 */
export const RedoCommandMenuItemComponent = memo(
  forwardRef(function RedoCommandMenuItemComponent(
    {
      dontRestrainIconWidth,
      item,
      indentForIcon,
      itemClicked,
      size = "sm",
      fitToAnchor = false,
    }: {
      dontRestrainIconWidth?: boolean;
      item: RedoCommandMenuItem;
      indentForIcon: boolean;
      itemClicked(): void;
      size?: RedoButtonSize;
      fitToAnchor?: boolean;
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const icon = item.Icon || (indentForIcon ? "placeholder" : undefined);

    const button = (
      <RedoButton
        buttonId={item.id}
        centerItems={false}
        className={
          fitToAnchor
            ? redoCommandMenuCss.fullWidthDropdownButton
            : redoCommandMenuCss.dropdownButton
        }
        dataTestId={item.dataTestId}
        disabled={item.disabled}
        dontRestrainIconWidth={dontRestrainIconWidth}
        hierarchy="tertiary"
        IconLeading={icon}
        IconTrailing={item.TrailingIcon}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          itemClicked();
          item.onClick?.(event);
        }}
        ref={ref}
        size={item.size ?? size}
        text={item.text}
        theme={item.theme}
        useOverflowTooltip
      />
    );

    const content = (
      <Flex
        align="center"
        className={redoCommandMenuCss.menuItemRow}
        data-theme={item.theme}
      >
        {button}
        {item.shortcutHotkey && (
          <RedoCommandShortcut hotkey={item.shortcutHotkey} />
        )}
      </Flex>
    );

    const tooltipText = (item.disabled && item.disabledTooltip) || item.tooltip;

    if (tooltipText) {
      return (
        <Tooltip
          arrow
          disableInteractive
          enterNextDelay={0}
          leaveDelay={0}
          PopperProps={{ sx: { pointerEvents: "none" } }}
          title={tooltipText}
        >
          <div>{content}</div>
        </Tooltip>
      );
    }

    return content;
  }),
);
